import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { Column } from './types';
import { Box, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Button from '../../components/button';
import EllipsisText from '../../components/text-ellipsis';
import { DataItem, SortOptions } from '../../types';
import { EMPTY_FIELD, FOOTER_HEIGHT } from '../../constants';
import DataTableCell from '../../components/table/components/table-cell';
import { debounce } from '@mui/material/utils';
import DataLoader from '../../components/data-loader';

export interface DataTableProps {
    columns: Column[];
    data: DataItem[];
    children?: React.ReactNode;
    rowsPerPage: number;
    page: number;
    setPage: (page: number) => void;
    setRowsPerPage: (rowsPerPage: number) => void;
    total: number;
    dataLoading?: boolean;
    resetFilters: () => void;
    setSorted?: (sortOptions: SortOptions | null) => void;
}

const loaderSX = { paddingBottom: '50px' };

const DataTable: React.FC<DataTableProps> = ({
    columns,
    data = [],
    children,
    page = 0,
    rowsPerPage = 10,
    setPage,
    setRowsPerPage,
    total = 0,
    dataLoading = false,
    setSorted,
    resetFilters,
}) => {
    const theme = useTheme();
    const isSMScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [sortOptions, setSortOptions] = useState<SortOptions | null>(null);
    const { t } = useTranslation();

    const handleChangePage = useCallback(
        (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
            setPage(newPage);
        },
        [setPage]
    );

    const handleChangeRowsPerPage = useMemo(
        () => (event: React.ChangeEvent<HTMLInputElement>) => {
            setRowsPerPage(+event.target.value);
            setPage(0);
        },
        [setRowsPerPage, setPage]
    );

    const tableRef = useRef<HTMLDivElement | null>(null);
    const [tableHeight, setTableHeight] = useState<number>(0);

    const calculateTableHeight = () => {
        if (tableRef.current) {
            const tableOffsetTop = tableRef.current.offsetTop;
            const newTableHeight = window.innerHeight - tableOffsetTop;
            setTableHeight(newTableHeight);
        }
    };

    const handleResize = debounce(calculateTableHeight, 100);

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);

    useEffect(() => {
        calculateTableHeight();
    }, [tableRef]);

    return (
        <>
            {children}
            <Box sx={{ position: 'relative' }} ref={tableRef}>
                <DataLoader
                    isLoading={dataLoading}
                    loadingMessage={<Typography sx={{ mt: 2 }}>{t('system-is-loading')}</Typography>}
                    showNoData={!dataLoading && data.length === 0}
                    noDataMessage={
                        <>
                            <Typography>{t('no-table-data-text')}</Typography>
                            {resetFilters && (
                                <Button
                                    variant='outlined'
                                    onClick={resetFilters}
                                    testId='reset-filters-button'
                                    sx={{ mt: 2 }}
                                >
                                    {t('go-to-all-data')}
                                </Button>
                            )}
                        </>
                    }
                    sx={loaderSX}
                />
                <TableContainer
                    component={Paper}
                    sx={{
                        height: `calc(${tableHeight}px - ${
                            isSMScreen ? FOOTER_HEIGHT.mobile : FOOTER_HEIGHT.desktop
                        } - 82px - 45px)`,
                        minHeight: '539px',
                        overflowX: 'auto',
                    }}
                >
                    <Table stickyHeader aria-label='customized table'>
                        <TableHead>
                            <TableRow>
                                {columns.map((props) => (
                                    <DataTableCell
                                        {...props}
                                        sortOptions={sortOptions}
                                        setSorted={setSorted}
                                        setSortOptions={setSortOptions}
                                        key={`column-header-${props.id}`}
                                    />
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((row, rowIndex) => (
                                <TableRow hover tabIndex={-1} key={rowIndex}>
                                    {columns.map(({ id, component: Component, sticky, alignHeader, width }) => {
                                        const value = row[id];

                                        return (
                                            <TableCell
                                                key={`column-row-${id}-${rowIndex}`}
                                                sx={{
                                                    padding: '0 16px',
                                                    ...(sticky && {
                                                        position: 'sticky',
                                                        left: sticky === 'left' ? 0 : 'unset',
                                                        right: sticky === 'right' ? 0 : 'unset',
                                                        zIndex: 2,
                                                    }),
                                                    width: width || 200,
                                                    maxWidth: width || 200,
                                                    minWidth: width || 200,
                                                    textAlign: alignHeader || 'left',
                                                }}
                                            >
                                                {Component ? (
                                                    <Component text={String(value)} {...row} />
                                                ) : (
                                                    <EllipsisText text={value || EMPTY_FIELD} />
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    data-testid='pagination'
                    rowsPerPageOptions={[10, 25, 100]}
                    component='div'
                    count={total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>
        </>
    );
};

export default memo(DataTable);
